/**
 * HistoryDropdown Component (AC-574)
 *
 * Provides quick access to recent entity changes with revert functionality.
 * Shows last 5 changes for the entity in a dropdown menu.
 *
 * Parent Components: AgentFormModal, ModelFormModal headers
 *
 * Features:
 * - Trigger button with history/clock icon
 * - Dropdown shows last 5 changes for the entity
 * - Loads data from versionStore.loadEntityHistory() when opened
 * - Each item shows: project badge, changed fields, relative time
 * - "Revert to this" button calls reapplyChange()
 * - "View Full History" link at bottom triggers onHistoryClick
 * - Closes on: outside click, Escape key, successful revert
 * - Loading and empty states
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { History, RotateCcw } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';
import { ChangeSummary, ChangeEntityType } from '../../types/version_control';

// Helper function to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return 'just now';
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  } else if (diffDay === 1) {
    return 'yesterday';
  } else if (diffDay < 30) {
    return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  } else {
    // Format as date for older items
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

interface HistoryDropdownProps {
  entityType: ChangeEntityType;
  entityId: number;
  projectId: number;
  onHistoryClick?: () => void;
}

const HistoryDropdown: React.FC<HistoryDropdownProps> = ({
  entityType,
  entityId,
  projectId,
  onHistoryClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [revertingId, setRevertingId] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { loadEntityHistory, entityHistory, reapplyChange } = useVersionStore();

  const historyKey = `${entityType}_${entityId}`;
  const allChanges = entityHistory.get(historyKey) || [];
  const changes = allChanges.slice(0, 5);

  // Load history when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      loadEntityHistory(entityType, entityId).finally(() => setLoading(false));
    }
  }, [isOpen, entityType, entityId, loadEntityHistory]);

  // Outside click handling
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Escape key handling
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Handle toggle
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Handle keyboard toggle
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
  }, []);

  // Handle revert
  const handleRevert = useCallback(
    async (changeId: number) => {
      setRevertingId(changeId);
      try {
        await reapplyChange(projectId, changeId);
        setIsOpen(false);
      } finally {
        setRevertingId(null);
      }
    },
    [reapplyChange, projectId]
  );

  // Handle full history click
  const handleFullHistoryClick = useCallback(() => {
    if (onHistoryClick) {
      onHistoryClick();
    }
    setIsOpen(false);
  }, [onHistoryClick]);

  // Render project badge
  const renderOperationBadge = (operation: string) => {
    const colors: Record<string, string> = {
      create: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      delete: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    };
    return (
      <span
        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
          colors[operation] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        }`}
      >
        {operation}
      </span>
    );
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger button */}
      <button
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        aria-label="Recent changes"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
      >
        <History className="w-5 h-5" />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-80 bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50"
        >
          {/* Header */}
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white">Recent Changes</h3>
          </div>

          {/* Content */}
          <div className="max-h-80 overflow-y-auto">
            {loading && changes.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
              </div>
            ) : changes.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">No recent changes</p>
              </div>
            ) : (
              <div className="py-1">
                {changes.map((change) => (
                  <div
                    key={change.change_id}
                    data-testid="history-item"
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 border-b border-gray-50 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {renderOperationBadge(change.operation)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatRelativeTime(change.created_at)}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRevert(change.change_id)}
                        disabled={revertingId === change.change_id}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Revert to this"
                      >
                        {revertingId === change.change_id ? (
                          <span>Reverting...</span>
                        ) : (
                          <>
                            <RotateCcw className="w-3 h-3" />
                            <span>Revert</span>
                          </>
                        )}
                      </button>
                    </div>
                    {change.changed_fields && change.changed_fields.length > 0 && (
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        Changed: {change.changed_fields.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={handleFullHistoryClick}
              className="w-full text-center text-sm font-medium text-primary-600 hover:text-primary-800"
              role="link"
              aria-label="View full history"
            >
              View Full History
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryDropdown;
